/**
 * SetupSection — the "HOW IT WORKS" / setup section, in REAL RetroUI + Tailwind
 * (light brutalist, StudySync brand). A filled red badge over a two-line
 * `font-head` headline (the second line in Teal), a centered subhead, then a
 * two-column row:
 *   LEFT  — a 3-step vertical timeline. Each step is an accent-colored square
 *           icon tile (white lucide glyph, hard 2px black border + offset shadow)
 *           next to a STEP 0X label, an uppercase title, and a muted description.
 *           A 2px black VERTICAL CONNECTOR LINE runs down the center of the tile
 *           column (behind the tiles, z-0), drawn like a timeline.
 *   RIGHT — the iPhone "Connected to Health" screenshot, height-capped.
 *
 * MOTION (framer-motion, scroll-triggered via whileInView):
 *   - Badge / headline / subhead fade + slide up.
 *   - The 3 step rows fade + slide up, STAGGERED (delay = index * 0.4) so they
 *     appear one after another (step 1 → 2 → 3), deliberately slow.
 *   - THE CONNECTOR LINE ANIMATES LAST: it draws downward (scaleY 0 → 1 from the
 *     top) on a delay (1.5s) that lands AFTER the third step has fully appeared,
 *     so the timeline visibly "draws" through the tiles once they're all present.
 *   - The phone fades + slides in slightly from the right.
 *
 * REDUCED MOTION: every animation is gated on `useReducedMotion()`. When it is
 * true we render EVERYTHING in its final visible state (opacity 1, no translate,
 * connector line fully drawn at scaleY 1) by passing `initial={false}` — nothing
 * is ever left stuck at opacity 0. This matters because the section is reviewed
 * via static reduced-motion screenshots.
 *
 * Tailwind Preflight is OFF in this project, so every border is declared
 * `border-solid` explicitly. ZERO MUI.
 */
import * as React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { FolderPlus, Link2, LineChart } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

/* ---------------------------------------------------------------------------
 * Step data. Each step carries its accent both as the tile BACKGROUND class and
 * as the matching label TEXT class. Classes are written out in full (not
 * composed) so Tailwind's content scan emits them.
 * ------------------------------------------------------------------------- */
interface Step {
  num: string;
  title: string;
  desc: string;
  Icon: LucideIcon;
  /** Tile background (the accent fill behind the white glyph). */
  tileBg: string;
  /** Matching "STEP 0X" label color. */
  labelColor: string;
}

const STEPS: Step[] = [
  {
    // STEP 01 — TEAL
    num: '01',
    title: 'Create or join a study',
    desc: 'Researchers spin up a study and share a code. Participants join in seconds — no account hoops.',
    Icon: FolderPlus,
    tileBg: 'bg-secondary',
    labelColor: 'text-secondary',
  },
  {
    // STEP 02 — CARDINAL
    num: '02',
    title: 'Connect a wearable',
    desc: 'Participants authorize Fitbit or Apple Health via OAuth. StudySync ingests securely from there.',
    Icon: Link2,
    tileBg: 'bg-primary',
    labelColor: 'text-primary',
  },
  {
    // STEP 03 — GREEN
    num: '03',
    title: 'Monitor & export',
    desc: 'Track enrollment and sync health in real time, then export analysis-ready datasets.',
    Icon: LineChart,
    tileBg: 'bg-green',
    labelColor: 'text-green',
  },
];

/* Per-step reveal delay → drives the 1-2-3 stagger. Deliberately slow so each
 * step lands distinctly, and the connector draws AFTER the last step finishes. */
const STEP_STAGGER = 0.4; // seconds between steps
const STEP_REVEAL_DURATION = 0.6; // seconds per step reveal
/* Last step starts at (STEPS.length-1)*STEP_STAGGER = 0.80s and finishes at
 * 0.80 + STEP_REVEAL_DURATION = 1.40s. CONNECTOR_DELAY (1.5s) starts the line
 * JUST AFTER all three steps are fully present, so it visibly draws downward
 * through the tiles only once every step has appeared. */
const CONNECTOR_DELAY = 1.5;

const SetupSection: React.FC = () => {
  const reduce = useReducedMotion();

  /* Shared "fade + slide up" reveal. With reduced motion we pass initial={false}
   * so the element mounts in its final state (no opacity:0 frame). */
  const fadeUp = (delay = 0) =>
    reduce
      ? ({ initial: false as const } as const)
      : ({
          initial: { opacity: 0, y: 24 },
          whileInView: { opacity: 1, y: 0 },
          viewport: { once: true, amount: 0.3 },
          transition: { duration: 0.6, ease: 'easeOut', delay },
        } as const);

  return (
    <section
      id="setup"
      className="scroll-mt-[88px] bg-background py-20 md:py-28"
    >
      <div className="mx-auto max-w-[1200px] px-4 md:px-8">
        {/* ----- Header: badge → headline → subhead (all centered) ----- */}
        <div className="text-center">
          {/* Outlined brutalist eyebrow chip. */}
          <motion.span
            {...fadeUp(0)}
            className="mx-auto block w-fit border-2 border-solid border-black bg-secondary px-3 py-1 font-mono text-xs font-bold uppercase tracking-[0.2em] text-white"
          >
            How it works
          </motion.span>

          {/* Two-line headline: line 1 near-black, line 2 Teal. */}
          <motion.h2
            {...fadeUp(0.08)}
            className="mt-6 font-head text-4xl uppercase leading-tight text-foreground md:text-6xl"
          >
            <span className="block">From setup to data</span>
            <span className="block text-secondary">in minutes</span>
          </motion.h2>

          {/* Subhead. */}
          <motion.p
            {...fadeUp(0.16)}
            className="mx-auto mt-4 max-w-xl font-sans text-muted-foreground"
          >
            Three simple steps to go from sign-up to analysis-ready data.
          </motion.p>
        </div>

        {/* ----- Two-column row: steps (left) + phone (right) ----- */}
        <div className="mt-16 grid items-center gap-12 lg:grid-cols-2 lg:gap-16">
          {/* LEFT: the 3-step timeline with the animated connector line. */}
          <div className="relative">
            {/*
              VERTICAL CONNECTOR LINE — a 2px black bar centered on the 14-wide
              (56px) tile column, i.e. left ≈ 27px. It sits BEHIND the tiles
              (z-0; tiles are z-10) and runs from the first tile to the last.
              Vertical insets of 1.75rem (= h-14 / 2) make it start at the center
              of the first tile and end at the center of the last tile.
              It ANIMATES LAST: scaleY 0 → 1 from the top, on CONNECTOR_DELAY so
              it draws downward AFTER all three steps have appeared.
            */}
            <motion.div
              aria-hidden
              className="absolute left-[27px] top-7 bottom-7 z-0 w-[2px] bg-black"
              style={{ transformOrigin: 'top' }}
              initial={reduce ? false : { scaleY: 0 }}
              whileInView={reduce ? undefined : { scaleY: 1 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={
                reduce
                  ? undefined
                  : { duration: 0.9, ease: 'easeInOut', delay: CONNECTOR_DELAY }
              }
            />

            {/* The three stacked steps. */}
            <div className="flex flex-col gap-12">
              {STEPS.map((step, i) => {
                const { num, title, desc, Icon, tileBg, labelColor } = step;
                return (
                  <motion.div
                    key={num}
                    className="flex items-start gap-5"
                    initial={reduce ? false : { opacity: 0, y: 24 }}
                    whileInView={reduce ? undefined : { opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.3 }}
                    transition={
                      reduce
                        ? undefined
                        : {
                            duration: STEP_REVEAL_DURATION,
                            ease: 'easeOut',
                            delay: i * STEP_STAGGER,
                          }
                    }
                  >
                    {/* Accent icon tile — sits ABOVE the connector line (z-10). */}
                    <span
                      className={`relative z-10 flex h-14 w-14 shrink-0 items-center justify-center border-2 border-solid border-black shadow-[4px_4px_0_0_#000] ${tileBg}`}
                    >
                      <Icon
                        className="text-white"
                        size={24}
                        strokeWidth={2.5}
                        aria-hidden
                      />
                    </span>

                    {/* Text block. */}
                    <div>
                      <p
                        className={`font-sans text-xs font-bold uppercase tracking-[0.2em] ${labelColor}`}
                      >
                        Step {num}
                      </p>
                      <h3 className="mt-1 font-head text-lg uppercase text-foreground md:text-xl">
                        {title}
                      </h3>
                      <p className="mt-2 max-w-md font-sans text-sm text-muted-foreground md:text-base">
                        {desc}
                      </p>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>

          {/* RIGHT: the iPhone screenshot. On mobile it sits ABOVE the steps. */}
          <motion.div
            className="order-first flex justify-center lg:order-none"
            initial={reduce ? false : { opacity: 0, x: 24 }}
            whileInView={reduce ? undefined : { opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={
              reduce ? undefined : { duration: 0.6, ease: 'easeOut', delay: 0.1 }
            }
          >
            {/* Portrait phone photo — already bezel'd, so no extra frame. */}
            <img
              src="/landing/health-app.png"
              alt="StudySync Health app — Connected to Health"
              className="mx-auto h-auto max-h-[560px] w-auto md:max-h-[640px]"
              loading="lazy"
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default SetupSection;
